/**
 * Czech (cs) translations.
 *
 * Same key shape as the Slovak source dictionary (`Dictionary` = `typeof sk`).
 * Brand name "Solandio" is treated as an indeclinable nominative and is never
 * declined in user-visible text.
 *
 * DUAL-MODE COPY: like the Slovak source, this dictionary ships BOTH the warm
 * family-mode wording and the co-parenting `*Separated` variants. The bare keys
 * default to the family-mode (warm) copy. ZERO legal vocabulary in family mode —
 * `soud`, `střídavka`, `předání`, `dohoda`, `50/50` appear ONLY in the explicit
 * `*Separated` strings.
 *
 * Do NOT change the `Dictionary` shape — only translate values.
 */
import { type Dictionary } from './sk';

export const cs: Dictionary = {
  appName: 'Solandio',
  tagline: 'Slunečná zóna pro vaše děti a rodinu.',

  language: {
    label: 'Jazyk',
    select: 'Vybrat jazyk',
  },

  common: {
    save: 'Uložit',
    saving: 'Ukládám…',
    cancel: 'Zrušit',
    add: 'Přidat',
    edit: 'Upravit',
    delete: 'Smazat',
    remove: 'Odebrat',
    close: 'Zavřít',
    loading: 'Načítám…',
    none: 'Žádné',
    optional: 'nepovinné',
    back: 'Zpět',
    today: 'Dnes',
    note: 'Poznámka',
    required: 'Toto pole je povinné.',
    error: 'Něco se pokazilo.',
    retry: 'Zkusit znovu',
    confirmDelete: 'Opravdu odebrat?',
    skipToContent: 'Přeskočit na obsah',
  },

  auth: {
    signInTitle: 'Vítejte v Solandio',
    signInLead:
      'Klidné, přehledné místo, kde má celá rodina pořádek — kalendář, děti, nákupy i náklady.',
    signInMicrosoft: 'Přihlásit se nebo se zaregistrovat',
    signInGoogle: 'Přihlásit se přes Google',
    signInMock: 'Přihlásit se (lokální vývoj)',
    signOut: 'Odhlásit se',
    signedInAs: 'Přihlášen/a jako',
    loadingSession: 'Ověřuji přihlášení…',
    warmingUp:
      'Připravujeme Solandio… při prvním otevření po delší době to může pár sekund trvat.',
    devNotice:
      'Lokální vývojový režim — bez Azure. Data jsou uložena jen ve vašem prohlížeči.',
  },

  nav: {
    home: 'Domů',
    children: 'Děti',
    contacts: 'Kontakty',
    health: 'Zdraví',
    calendar: 'Kalendář',
    shopping: 'Nákupy',
    documents: 'Dokumenty',
    messages: 'Vzkazy',
    expenses: 'Náklady',
    agreements: 'Dohody',
    sharing: 'Rodina',
    audit: 'Audit',
    settings: 'Nastavení',
    modules: 'Moduly',
    menu: 'Nabídka',
  },

  settings: {
    title: 'Nastavení',
    lead: 'Spravujte svůj profil, jazyk a přihlášení.',
    profile: 'Profil',
    displayName: 'Zobrazované jméno',
    displayNameHint: 'Toto jméno vidí ostatní členové rodiny.',
    email: 'E-mail',
    emailHint: 'E-mail nelze změnit — je svázán s vaším přihlášením.',
    nameInvalid: 'Zadejte jméno (1–120 znaků).',
    saved: 'Uloženo.',
    preferences: 'Předvolby',
    account: 'Účet',
    signOutHint: 'Odhlaste se z tohoto zařízení.',
  },

  profileSetup: {
    title: 'Vítejte v Solandio!',
    lead: 'Než začneme, řekněte nám, jak vám máme říkat.',
    nameLabel: 'Jak vám máme říkat?',
    nameHint: 'Toto jméno uvidí ostatní členové rodiny. Můžete ho kdykoli změnit v Nastavení.',
    namePlaceholder: 'např. Jana',
    nameInvalid: 'Zadejte jméno (1–120 znaků).',
    submit: 'Pokračovat',
  },

  family: {
    switcher: 'Rodina',
    create: 'Vytvořit rodinu',
    createTitle: 'Vytvořte si rodinu',
    createLead:
      'Rodina je váš společný prostor — pro partnera, děti i prarodiče. Vy rozhodujete, kdo co vidí.',
    nameLabel: 'Název rodiny',
    namePlaceholder: 'např. Naše rodina',
    empty: 'Zatím nemáte žádnou rodinu.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Dobré ráno, {name}',
    greetingDay: 'Dobrý den, {name}',
    greetingEvening: 'Dobrý večer, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Vítejte doma',
    add: 'Přidat',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Dnes',
    childrenTitle: 'Děti',
    shoppingTitle: 'Nákupy',
    expensesTitle: 'Náklady domácnosti',
    lastNoteTitle: 'Poslední vzkaz',
    /** Small links/summaries under sections. */
    tomorrow: 'Zítra',
    tomorrowEvents: 'Zítra: {count} události',
    viewAll: 'Zobrazit vše',
    nothingToday: 'Dnes nic v kalendáři.',
    noChildrenYet: 'Zatím tu není žádné dítě.',
    noShoppingYet: 'Žádný nákupní seznam.',
    noExpensesYet: 'Tento měsíc zatím žádné náklady.',
    noNotesYet: 'Zatím žádné vzkazy.',
    thisMonth: 'Tento měsíc',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} nevyrovnaných',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Vítejte doma 👋',
    emptyLead: 'Začněte třemi kroky a máte rodinu na jednom místě.',
    emptyAddChild: 'Přidejte první dítě',
    emptyInvitePartner: 'Pozvěte partnera',
    emptyCreateList: 'Vytvořte nákupní seznam',
  },

  children: {
    title: 'Děti',
    add: 'Přidat dítě',
    addTitle: 'Přidat dítě',
    none: 'Zatím tu není žádné dítě.',
    noneLead: 'Přidejte první dítě — narozeniny, zdraví i kontakty budete mít po ruce.',
    nameLabel: 'Jméno',
    namePlaceholder: 'např. Ema',
    emojiLabel: 'Avatar (emoji)',
    birthDateLabel: 'Datum narození',
    ageYears: 'let',
    selectPrompt: 'Vyberte dítě nahoře.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Profil dítěte',
    profileLead: 'Všechno o dítěti na jednom místě — jedním kliknutím.',
    quickHealth: 'Zdraví',
    quickContacts: 'Kontakty',
    quickCalendar: 'Kalendář',
    quickDocuments: 'Dokumenty',
    birthday: 'Narozeniny',
    ageLabel: 'Věk',
    noBirthDate: 'Datum narození není vyplněno.',
    editTitle: 'Upravit dítě',
    removeConfirm: 'Opravdu odebrat toto dítě? Jeho záznamy zůstanou zachovány.',
  },

  contacts: {
    title: 'Kontakty',
    add: 'Přidat kontakt',
    empty: 'Zatím žádné kontakty.',
    typeLabel: 'Typ',
    labelLabel: 'Název',
    labelPlaceholder: 'např. MUDr. Nováková',
    phoneLabel: 'Telefon',
    addressLabel: 'Adresa',
    types: {
      doctor: 'Lékař',
      school: 'Škola',
      dentist: 'Zubař',
      activity: 'Kroužek',
      other: 'Jiné',
    },
  },

  health: {
    title: 'Zdravotní záznam',
    sensitive: 'Citlivé údaje (GDPR čl. 9)',
    lead: 'Důležité zdravotní informace na jednom místě — pro případ nouze.',
    allergies: 'Alergie',
    medications: 'Léky',
    insurance: 'Pojišťovna',
    bloodType: 'Krevní skupina',
    conditions: 'Diagnózy / stavy',
    notes: 'Poznámky',
    updatedAt: 'Naposledy upraveno',
    placeholderNone: 'Nevyplněno',
  },

  calendar: {
    title: 'Kalendář',
    add: 'Přidat událost',
    empty: 'Zatím žádné události.',
    upcoming: 'Nejbližší',
    titleLabel: 'Název události',
    typeLabel: 'Typ',
    startLabel: 'Začátek',
    endLabel: 'Konec',
    allDayLabel: 'Celý den',
    locationLabel: 'Místo',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Střídavá péče',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Termín',
      handover: 'Jiné',
      custody: 'Jiné',
      school: 'Škola',
      activity: 'Aktivita',
      other: 'Jiné',
    },
    typesSeparated: {
      appointment: 'Termín',
      handover: 'Předání',
      custody: 'Střídání',
      school: 'Škola',
      activity: 'Aktivita',
      other: 'Jiné',
    },
  },

  custody: {
    title: 'Rozvrh střídání',
    none: 'Rozvrh střídání zatím není nastaven.',
    patternLabel: 'Vzor',
    startLabel: 'Začátek rozvrhu',
    notesLabel: 'Poznámky',
    set: 'Nastavit rozvrh',
    update: 'Upravit rozvrh',
    parentA: 'Rodič A',
    parentB: 'Rodič B',
    thisWeek: 'Tento týden',
    patterns: {
      'week-week': 'Týden / týden',
      '2-2-3': '2-2-3',
      custom: 'Vlastní',
    },
  },

  documents: {
    title: 'Dokumenty',
    sensitive: 'Citlivé',
    add: 'Přidat dokument',
    empty: 'Zatím žádné dokumenty.',
    nameLabel: 'Název',
    categoryLabel: 'Kategorie',
    fileLabel: 'Soubor',
    fileHint: 'Soubor se v lokálním režimu nenahrává — uloží se jen metadata.',
    uploadedBy: 'Nahrál/a',
    size: 'Velikost',
  },

  shopping: {
    title: 'Nákupy',
    /** List picker + creation. */
    listLabel: 'Seznam',
    newList: 'Nový seznam',
    newListTitle: 'Nový nákupní seznam',
    listNameLabel: 'Název seznamu',
    listNamePlaceholder: 'např. Potraviny',
    renameList: 'Přejmenovat seznam',
    deleteList: 'Smazat seznam',
    deleteListConfirm: 'Opravdu smazat tento seznam i se všemi položkami?',
    noLists: 'Zatím žádný seznam.',
    noListsLead: 'Vytvořte první seznam — například „Potraviny“ nebo „Drogerie“.',
    /** Item add + list. */
    addItem: 'Přidat položku',
    addItemPlaceholder: 'Přidat položku…',
    itemNameLabel: 'Položka',
    empty: 'Seznam je prázdný.',
    emptyLead: 'Přidejte první položku výše.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Kategorie',
    categoryNone: 'Bez kategorie',
    categoryPlaceholder: 'např. Ovoce a zelenina',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Kdo koupí',
    assigneeNone: 'Kdokoli',
    /** Buy-off. */
    bought: 'Koupeno',
    markBought: 'Označit jako koupené',
    clearBought: 'Smazat koupené',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Smazat koupené ({count})',
    clearBoughtConfirm: 'Smazat všechny koupené položky z tohoto seznamu?',
    itemCount: '{count} položek',
  },

  messages: {
    /**
     * "Vzkazy" in family mode (warm) / "Deník zpráv" in separated (court-log).
     * Bare `title`/`lead`/`empty` default to the family wording; the `*Family` and
     * `*Separated` variants let the mode-aware UI pick explicitly. Under the hood
     * messages stay append-only in BOTH modes.
     */
    title: 'Vzkazy',
    titleFamily: 'Vzkazy',
    titleSeparated: 'Deník zpráv',
    lead: 'Krátké rodinné vzkazy a poznámky — například „Vyzvedni Emu v 15:00“.',
    leadFamily: 'Krátké rodinné vzkazy a poznámky — například „Vyzvedni Emu v 15:00“.',
    leadSeparated:
      'Neměnný, soudně použitelný záznam komunikace. Zprávy nelze upravit ani smazat.',
    placeholder: 'Napište vzkaz…',
    placeholderSeparated: 'Napište zprávu…',
    send: 'Odeslat',
    empty: 'Zatím žádné vzkazy. Napište první.',
    emptyFamily: 'Zatím žádné vzkazy. Napište první.',
    emptySeparated: 'Deník je zatím prázdný. Napište první zprávu.',
    newest: 'Nejnovější',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Append-only — bez úprav a mazání.',
  },

  expenses: {
    /**
     * "Náklady domácnosti" (household budgeting) in family mode vs "Náklady"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Náklady domácnosti',
    titleFamily: 'Náklady domácnosti',
    titleSeparated: 'Náklady',
    lead: 'Přehled o tom, kdo co zaplatil — společný rozpočet domácnosti.',
    leadSeparated: 'Společné náklady na děti a jejich rozdělení mezi rodiče.',
    add: 'Přidat náklad',
    empty: 'Zatím žádné náklady.',
    amountLabel: 'Částka',
    categoryLabel: 'Kategorie',
    descriptionLabel: 'Popis',
    childLabel: 'Dítě',
    childAll: 'Celá rodina',
    incurredOnLabel: 'Datum',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Rozdělení',
    split5050: 'Napůl (50/50)',
    splitCustom: 'Vlastní',
    payerShare: 'Váš podíl',
    otherShare: 'Podíl druhého rodiče',
    /** Family mode shows only "kdo zaplatil" without any split. */
    paidBy: 'Kdo zaplatil',
    paidByFamily: 'Kdo zaplatil',
    settled: 'Vyrovnáno',
    outstanding: 'Nevyrovnáno',
    settle: 'Označit jako vyrovnané',
    totalOutstanding: 'Nevyrovnáno celkem',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Celkem tento měsíc',
    you: 'Vy',
  },

  agreements: {
    title: 'Dohody',
    lead:
      'Písemné dohody mezi rodiči. Jeden rodič dohodu navrhne, druhý ji schválí nebo odmítne. Schválená dohoda je neměnný, soudně použitelný záznam.',
    propose: 'Navrhnout dohodu',
    proposeTitle: 'Navrhnout dohodu',
    proposeLead:
      'Zformulujte dohodu jasně. Druhý rodič ji bude moci schválit nebo odmítnout.',
    proposeNote:
      'Po schválení se dohoda uzamkne a už ji nelze upravit ani smazat.',
    titleLabel: 'Název dohody',
    titlePlaceholder: 'např. Letní prázdniny 2026',
    bodyLabel: 'Znění dohody',
    bodyPlaceholder: 'Popište, na čem jste se dohodli…',
    empty: 'Zatím žádné dohody.',
    emptyLead: 'Navrhněte první dohodu druhému rodiči.',
    proposedBy: 'Navrhl/a',
    respondedBy: 'Vyřídil/a',
    accept: 'Schválit',
    decline: 'Odmítnout',
    withdraw: 'Stáhnout',
    locked: 'Uzamčeno — bez dalších změn.',
    actionStale: 'Dohoda už byla mezitím vyřízena. Seznam jsme obnovili.',
    you: 'Vy',
    status: {
      proposed: 'Navrženo',
      accepted: 'Schváleno',
      declined: 'Odmítnuto',
      withdrawn: 'Staženo',
    },
  },

  sharing: {
    title: 'Rodina',
    lead:
      'Podívejte se, kdo patří do rodiny, a rozhodněte, co každý vidí. Babičku či dědečka můžete pozvat jako důvěryhodnou osobu — uvidí jen to, co dovolíte.',
    familyNameLabel: 'Název rodiny',
    rename: 'Přejmenovat',
    renameTitle: 'Přejmenovat rodinu',
    members: 'Členové rodiny',
    invite: 'Pozvat',
    inviteMember: 'Pozvat člena',
    inviteTitle: 'Pozvat člena rodiny',
    emailLabel: 'E-mail',
    roleLabel: 'Role v rodině',
    roleParent: 'Rodič',
    roleParentDesc: 'Plný přístup ke všemu — pro druhého rodiče.',
    roleTrusted: 'Důvěryhodná osoba',
    roleTrustedDesc:
      'Například babička či dědeček — vidí jen to, co zaškrtnete.',
    scopesLabel: 'Co může vidět',
    scopesHint:
      'Rodič vidí vše. Důvěryhodná osoba vidí jen zaškrtnuté oblasti.',
    parentScopesNote: 'Rodič má automaticky plný přístup ke všem oblastem.',
    sensitiveNote: 'Citlivé — uděluje se jen výslovně.',
    seesLabel: 'Vidí',
    seesNothing: 'Zatím nemá přístup k žádné oblasti.',
    fullAccess: 'Plný přístup',
    status: {
      invited: 'Pozván/a',
      active: 'Aktivní',
    },
    you: 'Vy',
    parentBadge: 'Rodič',
    trustedBadge: 'Důvěryhodná osoba',
    changeScopes: 'Upravit přístup',
    removeMember: 'Odebrat',
    removeConfirm: 'Opravdu odebrat tuto osobu z rodiny?',
    onlyParents: 'Členy rodiny a jejich přístup mohou spravovat jen rodiče.',
  },

  scopes: {
    children: 'Děti',
    calendar: 'Kalendář',
    contacts: 'Kontakty',
    health: 'Zdraví',
    documents: 'Dokumenty',
    messages: 'Vzkazy',
    expenses: 'Náklady',
    shopping: 'Nákupy',
  },

  mode: {
    /** Settings → "Režim rodiny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Režim rodiny',
    cardLead:
      'Solandio je nastaveno pro rodinu, která žije spolu. Pokud žijete v samostatných domácnostech, můžete přepnout režim — všechna vaše data a celá historie dětí zůstávají nedotčena.',
    /** Current-mode labels. */
    familyLabel: 'Společná domácnost',
    familyDesc: 'Kalendář, děti, nákupy a náklady pro celou rodinu.',
    separatedLabel: 'Oddělené domácnosti',
    separatedDesc:
      'Přidá střídavou péči, rozdělení nákladů, dohody a deník zpráv.',
    currentLabel: 'Aktuální režim',
    /** Switch actions. */
    switchToSeparated: 'Přepnout na oddělené domácnosti',
    switchToFamily: 'Přepnout zpět na společnou domácnost',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Přepnout do režimu pro oddělené domácnosti?',
    confirmToSeparatedLead:
      'Přidáme nástroje pro rodiče, kteří žijí odděleně — střídavou péči, rozdělení nákladů, dohody a deník zpráv.',
    confirmToSeparatedNote:
      'Všechna vaše data a celá historie dětí zůstávají nedotčena. Režim můžete kdykoli přepnout zpět.',
    confirmToSeparatedAction: 'Ano, přepnout',
    confirmToFamilyTitle: 'Přepnout zpět na společnou domácnost?',
    confirmToFamilyLead:
      'Nástroje pro oddělené domácnosti se skryjí. Záznamy zůstávají uložené — při dalším přepnutí je najdete tak, jak byly.',
    confirmToFamilyAction: 'Ano, přepnout zpět',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Přepínám režim…',
    switched: 'Režim se změnil.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Režim rodiny mohou měnit jen rodiče.',
  },

  audit: {
    title: 'Audit',
    lead: 'Kdo, co a kdy změnil. Záznam je neměnný (append-only).',
    empty: 'Zatím žádné záznamy.',
    onlyParents: 'Audit mohou vidět jen rodiče.',
  },

  errors: {
    forbidden: 'K této oblasti nemáte oprávnění.',
    notFound: 'Položka nebyla nalezena.',
    generic: 'Akce selhala. Zkuste to znovu.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Spustit průvodce',
    help: 'Průvodce',
    helpHint: 'Rychlé představení aplikace si můžete přehrát kdykoli znovu.',
    /** Joyride control labels. */
    back: 'Zpět',
    next: 'Dále',
    last: 'Hotovo',
    skip: 'Přeskočit',
    close: 'Zavřít',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: 'Krok {step} z {steps}',
    steps: {
      welcomeTitle: 'Vítejte v Solandio',
      welcome: 'Za pár vteřin vám vše ukážeme.',
      childrenTitle: 'Děti',
      children: 'Tady přidáte a přepínáte děti.',
      modulesTitle: 'Vše o rodině',
      modules:
        'Vlevo najdete vše pro rodinu — kalendář, děti, zdraví, nákupy, náklady, dokumenty a vzkazy.',
      familyTitle: 'Rodina',
      family:
        'Tady pozvete partnera nebo babičku/dědečka a nastavíte, co vidí.',
      settingsTitle: 'Nastavení',
      settings: 'V Nastavení si změníte jméno a jazyk.',
      finishTitle: 'Hotovo!',
      finish: 'Začněte přidáním dítěte nebo nákupního seznamu.',
    },
  },
};
